// src/components/MatchCard.jsx (fixed - tambahkan import formatLocalTime)
import { Link } from 'react-router-dom';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Clock, 
  MapPin, 
  Tv, 
  Globe,
  Bell,
  ChevronRight,
  Sparkles,
  Timer,
  Hourglass,
  Zap,
  Play
} from 'lucide-react';
import { 
  formatMatchTime, 
  getUserTimezone, 
  shouldShowMatch, 
  getMatchStatus,
  getTimeRemaining,
  formatLocalTime,  // <--- TAMBAHKAN INI
  formatLocalDate 
} from '../utils/timeUtils';

const MatchCard = ({ match, index }) => {
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [currentTimezone, setCurrentTimezone] = useState('');
  const [showCountdownDetail, setShowCountdownDetail] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [matchStatus, setMatchStatus] = useState(() => getMatchStatus(match));
  
  const countdownIntervalRef = useRef(null);
  const visibilityIntervalRef = useRef(null);
  const statusIntervalRef = useRef(null);
  const isMountedRef = useRef(true);

  // Detect user timezone sekali saja
  useEffect(() => {
    setCurrentTimezone(getUserTimezone());
    
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Handle custom show/hide visibility berdasarkan showStart/showEnd dari data match
  useEffect(() => {
    if (match.showStart || match.showEnd) {
      const checkVisibility = () => {
        if (!isMountedRef.current) return;
        
        const shouldBeVisible = shouldShowMatch(match);
        
        if (shouldBeVisible !== isVisible) {
          setIsVisible(shouldBeVisible);
        }
      };
      
      checkVisibility();
      visibilityIntervalRef.current = setInterval(checkVisibility, 5000);
      
      return () => {
        if (visibilityIntervalRef.current) {
          clearInterval(visibilityIntervalRef.current);
        }
      };
    }
  }, [match.showStart, match.showEnd, match, isVisible]);

  // Update status secara periodik
  useEffect(() => {
    const updateStatus = () => {
      if (!isMountedRef.current) return;
      
      const newStatus = getMatchStatus(match);
      if (newStatus !== matchStatus) {
        setMatchStatus(newStatus);
      }
    };
    
    updateStatus();
    statusIntervalRef.current = setInterval(updateStatus, 1000);
    
    return () => {
      if (statusIntervalRef.current) {
        clearInterval(statusIntervalRef.current);
      }
    };
  }, [match, matchStatus]);

  // Countdown timer untuk upcoming
  useEffect(() => {
    if (matchStatus === 'upcoming') {
      const updateCountdown = () => {
        if (!isMountedRef.current) return;
        
        const remaining = getTimeRemaining(match);
        setTimeRemaining(remaining);
      };

      updateCountdown();
      countdownIntervalRef.current = setInterval(updateCountdown, 1000);
      
      return () => {
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
      };
    } else {
      setTimeRemaining(null);
    }
  }, [match, matchStatus]);

  // Format waktu kickoff
  const kickoff = useMemo(() => formatMatchTime(match), [match]);

  const statusColors = {
    live: 'from-red-500 to-red-600',
    finished: 'from-gray-500 to-gray-600',
    upcoming: 'from-football-green-500 to-football-green-600'
  };

  const watchLink = useMemo(() => `/event/live?id=${match.id}`, [match.id]);

  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -50, scale: 0.9 }}
      transition={{ 
        delay: index * 0.1,
        type: "spring",
        stiffness: 100,
        damping: 15
      }}
      whileHover={{ 
        scale: 1.02,
        y: -8,
        boxShadow: "0 30px 35px -10px rgba(34, 197, 94, 0.2), 0 8px 10px -6px rgba(0,0,0,0.3)"
      }}
      className="match-card group relative overflow-hidden"
      layout
    >
      {/* Animated Gradient Border */}
      <motion.div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{
          background: 'linear-gradient(45deg, #22c55e, #3b82f6, #8b5cf6, #22c55e)',
          backgroundSize: '400% 400%',
          filter: 'blur(8px)',
          zIndex: -1
        }}
        animate={{
          backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "linear"
        }}
      />

      <div className="relative z-10">
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center space-x-2">
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              className={`px-3 py-1.5 bg-gradient-to-r ${statusColors[matchStatus]} text-white text-xs font-bold rounded-full flex items-center space-x-1.5 shadow-lg`}
            >
              {matchStatus === 'live' && <Zap className="w-3 h-3" />}
              {matchStatus === 'finished' && <span>●</span>}
              {matchStatus === 'upcoming' && <Timer className="w-3 h-3" />}
              <span>
                {matchStatus === 'live' && `LIVE`}
                {matchStatus === 'finished' && 'FT'}
                {matchStatus === 'upcoming' && 'UPCOMING'}
              </span>
            </motion.div>
            
            {match.isBigMatch && (
              <motion.span
                initial={{ rotate: -10 }}
                animate={{ rotate: 0 }}
                className="px-3 py-1.5 bg-gradient-to-r from-yellow-400 to-amber-600 text-white text-xs font-bold rounded-full shadow-lg flex items-center space-x-1"
              >
                <Sparkles className="w-3 h-3" />
                <span>BIG MATCH</span>
              </motion.span>
            )}
          </div>
          
          <motion.span 
            className="text-white/60 text-sm font-medium px-2 py-1 rounded-lg bg-white/5"
          >
            {match.league}
          </motion.span>
        </div>

        {/* Teams */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex flex-col items-center space-y-3 flex-1">
            <div className="team-logo relative">
              <div className="absolute inset-0 bg-football-green-500 rounded-full blur-md opacity-50" />
              <span className="relative z-10">{match.homeTeam.logo}</span>
            </div>
            <span className="font-bold text-center text-white">
              {match.homeTeam.name}
            </span>
          </div>

          <div className="flex flex-col items-center px-4">
            {matchStatus === 'finished' ? (
              <div className="text-center">
                <span className="text-3xl font-bold text-white">
                  {match.score.home} - {match.score.away}
                </span>
                <span className="text-white/60 text-sm block mt-1">Final</span>
              </div>
            ) : matchStatus === 'live' ? (
              <div className="text-center">
                <span className="text-2xl font-bold text-red-500 flex items-center">
                  <span className="w-2 h-2 bg-red-500 rounded-full mr-2 animate-pulse" />
                  LIVE
                </span>
                <span className="text-white/60 text-sm block mt-1">Sedang Berlangsung</span>
              </div>
            ) : (
              <div className="text-center">
                <span className="text-sm text-football-green-400 font-medium mb-1 block">
                  Kickoff
                </span>
                <span className="text-2xl font-bold text-white">
                  {kickoff.localTime}
                </span>
                <span className="text-white/60 text-xs block mt-1">
                  {kickoff.isToday ? 'Hari Ini' : kickoff.isTomorrow ? 'Besok' : kickoff.relativeTime}
                </span>
              </div>
            )}
          </div>

          <div className="flex flex-col items-center space-y-3 flex-1">
            <div className="team-logo relative">
              <div className="absolute inset-0 bg-football-green-500 rounded-full blur-md opacity-50" />
              <span className="relative z-10">{match.awayTeam.logo}</span>
            </div>
            <span className="font-bold text-center text-white">
              {match.awayTeam.name}
            </span>
          </div>
        </div>

        {/* Match Info */}
        <div className="space-y-2 text-white/80 text-sm mb-4">
          <div className="flex items-center space-x-2 bg-white/5 p-2 rounded-lg">
            <Clock className="w-4 h-4 text-football-green-400" />
            <span className="font-medium">{kickoff.localTime}</span>
            <span className="text-white/40 mx-1">•</span>
            <Globe className="w-3 h-3 text-white/60" />
            <span className="text-xs text-white/60">{currentTimezone}</span>
          </div>
          
          <div className="flex items-center space-x-2 bg-white/5 p-2 rounded-lg">
            <MapPin className="w-4 h-4 text-football-green-400" />
            <span>{match.venue}</span>
          </div>
          
          <div className="flex items-center space-x-2 bg-white/5 p-2 rounded-lg">
            <Tv className="w-4 h-4 text-football-green-400" />
            <span>{match.tvChannel}</span>
          </div>
        </div>

        {/* Info Jadwal Tayang - FIXED: menggunakan formatLocalTime yang sudah diimport */}
        {match.showStart && match.showEnd && (
          <div className="text-[10px] text-white/30 text-right mb-2">
            <span>
              Tayang: {formatLocalTime(match.showStart)} - {formatLocalTime(match.showEnd)} ({currentTimezone})
            </span>
          </div>
        )}

        {/* Countdown untuk upcoming */}
        {matchStatus === 'upcoming' && timeRemaining && !timeRemaining.isLive && (
          <div className="mt-4 mb-4">
            <div 
              className="relative overflow-hidden rounded-2xl p-4 cursor-pointer"
              onClick={() => setShowCountdownDetail(!showCountdownDetail)}
            >
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-football-green-600/30 via-blue-600/30 to-purple-600/30"
                animate={{
                  backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
                }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "linear"
                }}
                style={{
                  backgroundSize: '200% 200%',
                }}
              />
              
              <div className="absolute inset-0 backdrop-blur-xl bg-black/20" />
              
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                    >
                      <Hourglass className="w-4 h-4 text-yellow-400" />
                    </motion.div>
                    <span className="text-white font-semibold text-sm">MENUJU PERTANDINGAN</span>
                  </div>
                  <motion.div
                    animate={{ rotate: showCountdownDetail ? 90 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ChevronRight className="w-4 h-4 text-white/80" />
                  </motion.div>
                </div>

                <div className="grid grid-cols-4 gap-2">
                  {timeRemaining.days > 0 && (
                    <div className="bg-white/10 backdrop-blur rounded-xl p-2 text-center">
                      <span className="text-2xl font-bold text-white block">
                        {timeRemaining.days}
                      </span>
                      <span className="text-[10px] text-white/70 uppercase tracking-wider">
                        Hari
                      </span>
                    </div>
                  )}
                  
                  <div className="bg-white/10 backdrop-blur rounded-xl p-2 text-center">
                    <span className="text-2xl font-bold text-white block">
                      {String(timeRemaining.hours).padStart(2, '0')}
                    </span>
                    <span className="text-[10px] text-white/70 uppercase tracking-wider">
                      Jam
                    </span>
                  </div>
                  
                  <div className="bg-white/10 backdrop-blur rounded-xl p-2 text-center">
                    <span className="text-2xl font-bold text-white block">
                      {String(timeRemaining.minutes).padStart(2, '0')}
                    </span>
                    <span className="text-[10px] text-white/70 uppercase tracking-wider">
                      Menit
                    </span>
                  </div>
                  
                  <div className="bg-white/10 backdrop-blur rounded-xl p-2 text-center">
                    <motion.span 
                      key={timeRemaining.seconds}
                      animate={{ scale: [1, 1.1, 1] }}
                      transition={{ duration: 1, repeat: Infinity }}
                      className="text-2xl font-bold text-white block"
                    >
                      {String(timeRemaining.seconds).padStart(2, '0')}
                    </motion.span>
                    <span className="text-[10px] text-white/70 uppercase tracking-wider">
                      Detik
                    </span>
                  </div>
                </div>

                <AnimatePresence>
                  {showCountdownDetail && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-3 pt-3 border-t border-white/20"
                    >
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="bg-white/5 rounded-lg p-2">
                          <span className="text-white/60 block">Tanggal</span>
                          <span className="text-white font-medium">{kickoff.localDate}</span>
                        </div>
                        <div className="bg-white/5 rounded-lg p-2">
                          <span className="text-white/60 block">Waktu</span>
                          <span className="text-white font-medium">{kickoff.localTime}</span>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        )}

        {/* WATCH LIVE BUTTON */}
        {matchStatus === 'live' && (
          <Link to={watchLink} className="block mt-4">
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="relative overflow-hidden rounded-xl bg-gradient-to-r from-red-500 to-red-600 p-4 shadow-lg shadow-red-500/30"
            >
              <div className="relative z-10 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <motion.div
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <Play className="w-6 h-6 text-white" />
                  </motion.div>
                  <div>
                    <p className="text-white font-bold text-lg">TONTON LIVE</p>
                    <p className="text-white/80 text-xs">Pertandingan sedang berlangsung</p>
                  </div>
                </div>
                <ChevronRight className="w-6 h-6 text-white" />
              </div>
            </motion.div>
          </Link>
        )}

        {/* REMINDER BUTTON */}
        {matchStatus === 'upcoming' && (
          <Link to={watchLink} className="block mt-4">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full px-4 py-3 bg-gradient-to-r from-football-green-500 to-football-green-600 hover:from-football-green-600 hover:to-football-green-700 text-white rounded-xl font-semibold transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg shadow-football-green-500/30"
            >
              <Bell className="w-4 h-4" />
              <span>Ingatkan Saya</span>
            </motion.button>
          </Link>
        )}

        {/* Timezone Indicator */}
        <div className="mt-3 text-[10px] text-white/40 text-right">
          Waktu {currentTimezone}
        </div>
      </div>
    </motion.div>
  );
};

export default MatchCard;